import {
    isPrimaryRoleId,
    PRIMARY_ROLE_OPTIONS,
    type PrimaryRoleId,
    RoleId,
} from '@common/constants';
import type { UserRole, UserWithLawFirm } from '@common/types/users';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import z from 'zod';
import LoadingButton from '@/components/Buttons/LoadingButton';
import ReadOnlyTextField from '@/components/ReadOnlyTextField';
import { useUsers } from '@/queries/users';
import { fetchUserById, updateUser } from '@/services/UserService';
import useIdentityStore from '@/stores/useIdentityStore';
import {
    canManageFirmUsers,
    getPrimaryRoleId,
    getRolesFromRolesArray,
    hasOrganizationalAdminRole,
} from '@/utils';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

const USER_SCHEMA = z.object({
    firstName: z.string('First name is required').min(1),
    middleName: z.string().nullable(),
    lastName: z.string('Last name is required').min(1),
    lawyerId: z.number().nullable(),
});

function buildRoleIds(roles: UserRole[] | undefined, primaryRoleId: PrimaryRoleId): number[] {
    const preservedRoleIds = (roles ?? [])
        .map((role) => role.roleId)
        .filter((roleId) => roleId !== RoleId.BASIC && !isPrimaryRoleId(roleId));

    return [RoleId.BASIC, primaryRoleId, ...preservedRoleIds];
}

export default function UserDetails() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { userId } = useParams();
    const parsedUserId = Number(userId);

    const loggedInUser = useIdentityStore((state) => state.user);
    const isSuperAdmin = !!loggedInUser?.roles?.some((role) => role.roleId === RoleId.SUPER_ADMIN);
    const isSelf = loggedInUser?.userId === parsedUserId;

    const [errors, setErrors] = useState<Record<string, string | undefined>>({});
    const [isSaving, setIsSaving] = useState(false);

    const { data: initialUserDetails } = useSuspenseQuery<UserWithLawFirm>({
        queryKey: ['user', parsedUserId],
        queryFn: async () => {
            const response = await fetchUserById(parsedUserId);
            return response.data;
        },
        staleTime: 5 * 60_000,
    });

    const canManageUser = canManageFirmUsers(loggedInUser, initialUserDetails.lawFirmId);
    const hasEditPermission = isSelf || canManageUser;
    // The backend doesn't let users change their own roles, except Super Admins
    const canEditRoles = canManageUser && (!isSelf || isSuperAdmin);

    const { data: users = [] } = useUsers({
        lawFirmId: initialUserDetails.lawFirmId,
    });

    const [userDetails, setUserDetails] = useState<UserWithLawFirm>(initialUserDetails);
    const selectedPrimaryRoleId = getPrimaryRoleId(userDetails.roles ?? []);
    const isOrgAdmin = hasOrganizationalAdminRole(userDetails.roles);

    const isDirty = useMemo(() => {
        return (
            userDetails.firstName !== initialUserDetails.firstName ||
            userDetails.middleName !== initialUserDetails.middleName ||
            userDetails.lastName !== initialUserDetails.lastName ||
            userDetails.lawyerId !== initialUserDetails.lawyerId ||
            selectedPrimaryRoleId !== getPrimaryRoleId(initialUserDetails.roles ?? []) ||
            isOrgAdmin !== hasOrganizationalAdminRole(initialUserDetails.roles)
        );
    }, [userDetails, initialUserDetails, selectedPrimaryRoleId, isOrgAdmin]);

    const changeHandler = (ev: { target: { name: string; value: string } }) => {
        if (ev.target.name && (ev.target.value || ev.target.value === '')) {
            setUserDetails((prev) => ({
                ...prev,
                [ev.target.name]: ev.target.value,
            }));
        }
    };

    const handlePrimaryRoleChange = (roleId: PrimaryRoleId) => {
        const selectedRole = PRIMARY_ROLE_OPTIONS.find((role) => role.roleId === roleId);
        if (!selectedRole) return;

        setUserDetails((prev) => ({
            ...prev,
            roles: [
                ...(prev.roles ?? []).filter((role) => !isPrimaryRoleId(role.roleId)),
                { roleId: selectedRole.roleId, name: selectedRole.name },
            ],
        }));
        setErrors((prev) => ({ ...prev, role: undefined }));
    };

    const handleOrgAdminChange = (checked: boolean) => {
        setUserDetails((prev) => {
            const rolesWithoutOrgAdmin = (prev.roles ?? []).filter(
                (role) => role.roleId !== RoleId.ORGANIZATIONAL_ADMIN,
            );
            return {
                ...prev,
                roles: checked
                    ? [
                          ...rolesWithoutOrgAdmin,
                          {
                              roleId: RoleId.ORGANIZATIONAL_ADMIN,
                              name: 'Organizational Admin',
                          },
                      ]
                    : rolesWithoutOrgAdmin,
            };
        });
    };

    const handleUndo = () => {
        setUserDetails(initialUserDetails);
        setErrors({});
    };

    const handleSave = async () => {
        const validationResult = USER_SCHEMA.safeParse(userDetails);
        if (!validationResult.success) {
            const fieldErrors: Record<string, string> = {};
            for (const issue of validationResult.error.issues) {
                const field = issue.path[0];
                if (typeof field === 'string' && !fieldErrors[field]) {
                    fieldErrors[field] = issue.message;
                }
            }
            setErrors(fieldErrors);
            return;
        }

        const rolesChanged =
            selectedPrimaryRoleId !== getPrimaryRoleId(initialUserDetails.roles ?? []) ||
            isOrgAdmin !== hasOrganizationalAdminRole(initialUserDetails.roles);

        setErrors({});
        setIsSaving(true);

        try {
            await updateUser(userDetails.userId, {
                firstName: userDetails.firstName,
                lastName: userDetails.lastName,
                middleName: userDetails.middleName,
                lawyerId: userDetails.lawyerId ?? null,
                ...(canEditRoles && rolesChanged
                    ? {
                          roleIds: selectedPrimaryRoleId
                              ? buildRoleIds(userDetails.roles, selectedPrimaryRoleId)
                              : [
                                    RoleId.BASIC,
                                    ...(userDetails.roles ?? [])
                                        .map((role) => role.roleId)
                                        .filter((roleId) => roleId !== RoleId.BASIC),
                                ],
                      }
                    : {}),
            });
            successSnackbar('User details updated successfully');
            await queryClient.invalidateQueries({ queryKey: ['user', parsedUserId] });
            await queryClient.invalidateQueries({ queryKey: ['users'] });
        } catch (error: unknown) {
            console.error(error);
            if (isAxiosError(error)) {
                errorSnackbar(error.response?.data || 'Something went wrong');
                setErrors((prev) => ({
                    ...prev,
                    ...error.response?.data,
                }));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const getAssignedLawyerName = () => {
        const lawyer = users.find((user) => user.userId === userDetails.lawyerId);
        return lawyer?.fullName ?? '-';
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box
                sx={{
                    backgroundColor: 'neutral.white',
                    padding: '16px 24px',
                    borderBottom: '1px solid',
                    borderColor: 'neutral.borderColor',
                }}
            >
                <Breadcrumbs
                    separator={<NavigateNextIcon fontSize="small" sx={{ color: 'neutral.gray' }} />}
                >
                    <Link
                        underline="none"
                        sx={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: 'neutral.gray',
                            lineHeight: '20px',
                            cursor: 'pointer',
                        }}
                        onClick={() => navigate('/users')}
                    >
                        Users
                    </Link>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                        }}
                    >
                        {userDetails.fullName ?? ''}
                    </Typography>
                </Breadcrumbs>
                <Stack spacing={0.5} mt={1}>
                    <Typography variant="h2">{userDetails.fullName}</Typography>
                    <Typography variant="body2">
                        {getRolesFromRolesArray(userDetails.roles ?? []) ?? 'Role not set'}
                    </Typography>
                </Stack>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Stack direction="row" m={3} spacing={3}>
                    <Stack
                        spacing={2}
                        p={3}
                        sx={{
                            border: '1px solid',
                            borderColor: 'neutral.borderColor',
                            borderRadius: '4px',
                            backgroundColor: 'neutral.white',
                            width: '100%',
                        }}
                    >
                        <Typography variant="h3">User Information</Typography>
                        <ReadOnlyTextField fullWidth value={userDetails.email} label="Email" />
                        {hasEditPermission ? (
                            <>
                                <TextField
                                    fullWidth
                                    value={userDetails.firstName ?? ''}
                                    variant="outlined"
                                    placeholder="e.g. Steven"
                                    label="First name"
                                    name="firstName"
                                    onChange={(event) => changeHandler(event)}
                                    error={!!errors.firstName}
                                    helperText={
                                        userDetails.firstName !== undefined && errors.firstName
                                    }
                                />
                                <TextField
                                    fullWidth
                                    value={userDetails.middleName ?? ''}
                                    variant="outlined"
                                    placeholder="e.g. Stark"
                                    label="Middle name(s)"
                                    name="middleName"
                                    error={!!errors.middleName}
                                    helperText={
                                        userDetails.middleName !== undefined && errors.middleName
                                    }
                                    onChange={(event) => changeHandler(event)}
                                />
                                <TextField
                                    fullWidth
                                    value={userDetails.lastName ?? ''}
                                    variant="outlined"
                                    placeholder="e.g. Hamilton"
                                    label="Last name"
                                    name="lastName"
                                    error={!!errors.lastName}
                                    helperText={
                                        userDetails.lastName !== undefined && errors.lastName
                                    }
                                    onChange={(event) => changeHandler(event)}
                                />
                            </>
                        ) : (
                            <>
                                <ReadOnlyTextField
                                    fullWidth
                                    value={userDetails.firstName ?? '-'}
                                    label="First name"
                                />
                                <ReadOnlyTextField
                                    fullWidth
                                    value={userDetails.middleName ?? '-'}
                                    label="Middle name(s)"
                                />
                                <ReadOnlyTextField
                                    fullWidth
                                    value={userDetails.lastName ?? '-'}
                                    label="Last name"
                                />
                            </>
                        )}
                    </Stack>
                    <Stack
                        spacing={2}
                        p={3}
                        sx={{
                            border: '1px solid',
                            borderColor: 'neutral.borderColor',
                            borderRadius: '4px',
                            backgroundColor: 'neutral.white',
                            width: '100%',
                        }}
                    >
                        <Typography variant="h3">Business Information</Typography>
                        <ReadOnlyTextField
                            fullWidth
                            value={userDetails.lawFirm?.companyName ?? '-'}
                            label="Law firm"
                        />
                        {canEditRoles ? (
                            <>
                                <FormControl fullWidth error={!!errors.role}>
                                    <InputLabel id="user-role">Role</InputLabel>
                                    <Select
                                        labelId="user-role"
                                        label="Role"
                                        value={selectedPrimaryRoleId ?? ''}
                                        onChange={(event) => {
                                            const roleId = Number(event.target.value);
                                            if (isPrimaryRoleId(roleId)) {
                                                handlePrimaryRoleChange(roleId);
                                            }
                                        }}
                                    >
                                        {PRIMARY_ROLE_OPTIONS.map((role) => (
                                            <MenuItem key={role.roleId} value={role.roleId}>
                                                {role.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
                                </FormControl>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isOrgAdmin}
                                            onChange={(event) =>
                                                handleOrgAdminChange(event.target.checked)
                                            }
                                        />
                                    }
                                    label="Organizational Admin"
                                />
                            </>
                        ) : (
                            <ReadOnlyTextField
                                fullWidth
                                value={getRolesFromRolesArray(userDetails.roles ?? []) ?? '-'}
                                label="Role"
                            />
                        )}
                        {hasEditPermission ? (
                            <FormControl fullWidth>
                                <InputLabel id="assigned-lawyer">Assigned lawyer</InputLabel>
                                <Select
                                    labelId="assigned-lawyer"
                                    label="Assigned lawyer"
                                    name="lawyerId"
                                    value={userDetails.lawyerId ?? ''}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setUserDetails((prev) => ({
                                            ...prev,
                                            lawyerId:
                                                value === '' ? null : (value as unknown as number),
                                        }));
                                    }}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {users
                                        .filter((user) =>
                                            user.roles?.some(
                                                (role) => role.roleId === RoleId.LAWYER,
                                            ),
                                        )
                                        .map((lawyer) => (
                                            <MenuItem key={lawyer.userId} value={lawyer.userId}>
                                                {lawyer.fullName}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <ReadOnlyTextField
                                fullWidth
                                value={getAssignedLawyerName()}
                                label="Assigned lawyer"
                            />
                        )}
                    </Stack>
                </Stack>
            </Box>
            {hasEditPermission && (
                <Box
                    sx={{
                        backgroundColor: 'neutral.white',
                        borderTop: '1px solid',
                        borderColor: 'neutral.borderColor',
                        padding: '16px 24px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                    }}
                >
                    <Button variant="outlined" onClick={handleUndo} disabled={!isDirty}>
                        Undo
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={handleSave}
                        loading={isSaving}
                        disabled={!isDirty}
                    >
                        Save
                    </LoadingButton>
                </Box>
            )}
        </Box>
    );
}
